import SaveAsTemplateButton from '../../../components/scribe/SaveAsTemplateButton';
import { scribeTokens as ui } from '../../../components/scribe/tokens';
import ReportSourceSearch from '../../MedicalScribe/ReportSourceSearch';
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Paper,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { 
  HelpOutline, 
  ArrowBack, 
  Save,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatSize,
  TextDecrease,
  TextIncrease,
  FormatListBulleted,
  FormatListNumbered,
  Image as ImageIcon,
  TableChart,
  Clear,
  DragIndicator,
  AddCircleOutline,
  RemoveCircleOutline,
  ViewColumn,
  ViewStream
} from "@mui/icons-material";
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { supabase } from "../../../supabaseClient"; 
import FileHandler from '@tiptap-pro/extension-file-handler';
import DragHandle from '@tiptap-pro/extension-drag-handle-react';
import useAlert from '../../../alert/useAlert.js';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
// import ImageResize from 'tiptap-extension-resize-image';
import { ImageResize } from "./ImageResize.ts";
import Highlight from '@tiptap/extension-highlight'
import { set } from "date-fns";
import Loader from "../../../loader.js";
import axios from "axios";
import HighlightIcon from '@mui/icons-material/Highlight';
import { toStorageKeySegment } from '../../../utils/storageKey.js';
import { v4 as uuidv4 } from 'uuid';

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: element => element.style.fontSize || null,
          renderHTML: attributes => {
            if (!attributes.fontSize) return {};
            return { style: `font-size: ${attributes.fontSize}` };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) =>
        chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize: () => ({ chain }) =>
        chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

// Move this outside as a utility function that can be accessed anywhere in the file
export const uploadImageToSupabase = async (file, userid) => {
  if (!file) return null;

  try {
    // Upload to Supabase storage (keys must be ASCII-safe)
    // A saved template can retain this image. A later upload with the same
    // display name must create a new object rather than overwrite its bytes.
    const fileName = `${uuidv4()}-${toStorageKeySegment(file.name)}`;
    const filePath = `template-images/${userid}/${fileName}`;
    
    const { error } = await supabase.storage
    .from('tikcare-attachments')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });
    
    if (error) throw error;
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('tikcare-attachments')
      .getPublicUrl(filePath);
    
    return urlData?.publicUrl || null;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
};

const MenuBar = ({ editor, userDetails }) => {
  if (!editor) {
    return null;
  }

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    const imageUrl = await uploadImageToSupabase(file, userDetails.id);
    
    if (imageUrl) {
      // Insert the image
      editor.chain().focus()
        .setImage({ 
          src: imageUrl,
          alt: file.name,
          title: file.name,
          userid: userDetails.id,
        })
        .run();
    }
  };

  const isInTable = editor.isActive('table');

  return (
    <Box 
      sx={{ 
        mb: 2, 
        border: `1px solid ${ui.colors.border}`,
        p: 1, 
        borderRadius: `${ui.radius.control}px`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: ui.colors.surface,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
        <Tooltip title="Bold">
          <IconButton 
            onClick={() => editor.chain().focus().toggleBold().run()}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: editor.isActive('bold') ? ui.colors.brandSoft : 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <FormatBold />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Italic">
          <IconButton 
            onClick={() => editor.chain().focus().toggleItalic().run()}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: editor.isActive('italic') ? ui.colors.brandSoft : 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <FormatItalic />
          </IconButton>
        </Tooltip>

        <Tooltip title="Underline">
          <IconButton 
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: editor.isActive('underline') ? ui.colors.brandSoft : 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <FormatUnderlined />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Decrease font size">
          <IconButton
            onClick={() => {
              const current = parseFloat(editor.getAttributes('textStyle').fontSize) || 16;
              editor.chain().focus().setFontSize(`${Math.max(8, current - 2)}px`).run();
            }}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <TextDecrease />
          </IconButton>
        </Tooltip>

        <Tooltip title="Increase font size">
          <IconButton
            onClick={() => {
              const current = parseFloat(editor.getAttributes('textStyle').fontSize) || 16;
              editor.chain().focus().setFontSize(`${Math.min(48, current + 2)}px`).run();
            }}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <TextIncrease />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Bullet List">
          <IconButton 
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: editor.isActive('bulletList') ? ui.colors.brandSoft : 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <FormatListBulleted />
          </IconButton>
        </Tooltip>

        <Tooltip title="Numbered List">
          <IconButton 
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            size="small"
            sx={{
              color: ui.colors.brand,
              bgcolor: editor.isActive('orderedList') ? ui.colors.brandSoft : 'transparent',
              ':hover': {
                bgcolor: ui.colors.brandSoft,
              },
            }}
          >
            <FormatListNumbered />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Insert Image">
          <IconButton 
            component="label" 
            size="small"
          >
            <ImageIcon />
            <input 
              type="file" 
              hidden 
              accept="image/*" 
              onChange={(e) => { handleImageUpload(e.target.files[0]) }} 
            />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Insert Table (3x3)">
          <IconButton
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run()}
            size="small"
            sx={{ color: ui.colors.brand }}
          >
            <TableChart />
          </IconButton>
        </Tooltip>
      </Stack>

      {isInTable && (
        <Stack 
          direction="row" 
          spacing={1} 
          flexWrap="wrap" 
          alignItems="center"
          sx={{ 
            mt: 1, 
            pt: 1, 
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            borderRadius: `${ui.radius.control}px`,
            px: 1,
            py: 0.5
          }}
        >
          <Tooltip title="Add Column Before">
            <IconButton
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              size="small"
              sx={{ color: ui.colors.brand }}
            >
              <AddCircleOutline />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Column After">
            <IconButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              size="small"
              sx={{ color: ui.colors.brand }}
            >
              <ViewColumn />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Column">
            <IconButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              size="small"
              sx={{ color: ui.colors.danger }}
            >
              <RemoveCircleOutline />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Row Before">
            <IconButton
              onClick={() => editor.chain().focus().addRowBefore().run()}
              size="small"
              sx={{ color: ui.colors.brand }}
            >
              <AddCircleOutline />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Row After">
            <IconButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              size="small"
              sx={{ color: ui.colors.brand }}
            >
              <ViewStream />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Row">
            <IconButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              size="small"
              sx={{ color: ui.colors.danger }}
            >
              <RemoveCircleOutline />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Entire Table">
            <IconButton
              onClick={() => editor.commands.deleteTable()}
              size="small"
              sx={{ color: ui.colors.danger }}
            >
              <Clear />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}

const TemplateView = ({ onTemplateSaved, patient_id, currentOrg, selectedReport, sectionSummary, setSelectedReport, setSectionSummary, setHistoryItems, reportTitle, templateId, templateTitle, templateContent, reportId, reportContent, handleBack, userDetails, view, edit, readOnly = false, highlightedIndex, setSelectedText, currentFileIndex, highlightMode = false, onDirtyChange, onSaveError, onCheckSources, checkingSources, ensureReport, reportTools }) => {
  const id = view === 'report' ? reportId : templateId;
  const isEditing = !!id;
  const templateReadOnly = view === 'template' && readOnly;
  // File-handler extensions retain callbacks; check the latest preview state.
  const templateReadOnlyRef = useRef(templateReadOnly);
  templateReadOnlyRef.current = templateReadOnly;

  const [title, setTitle] = useState('');
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setAlert } = useAlert();
  const isInitialMount = useRef(true);
  const isSettingContentRef = useRef(false);
  const saveTimerRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const pendingSavesRef = useRef(0);
  const pendingSaveRef = useRef(null);
  const lastSavedContentRef = useRef('');

  const handleEditorSelection = () => {
    if (!setSelectedText || !highlightMode) {
      return;
    }
    if (editor) {
      const { state } = editor;
      const { from, to } = state.selection;
      
      if (from !== to) {
        const selectedContent = state.doc.textBetween(from, to, ' ');
        setSelectedText(selectedContent);
      }
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight.configure({ multicolor: true }),
      // ResizableImageWithDelete.configure({
      //   inline: true,
      //   allowBase64: true,
      // }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      FontSize,
      Underline,
      FileHandler.configure({
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
        onDrop: async (currentEditor, files, pos) => {
          if (templateReadOnlyRef.current) return;
          for (const file of files) {
            // Upload directly to Supabase without showing temporary version
            const imageUrl = await uploadImageToSupabase(file, userDetails.id);
            
            if (imageUrl && !templateReadOnlyRef.current) {
              // Insert the image at the drop position with the permanent URL
              currentEditor.chain().insertContentAt(pos, {
                type: 'image',
                attrs: {
                  src: imageUrl,
                  alt: file.name,
                  title: file.name,
                  userid: userDetails.id,
                },
              }).focus().run()
            }
          }
        },
        onPaste: async (currentEditor, files, htmlContent) => {
          if (templateReadOnlyRef.current) return;
          if (htmlContent) {
            return false;
          }
          
          for (const file of files) {
            // Upload directly to Supabase
            const imageUrl = await uploadImageToSupabase(file, userDetails.id);
            
            if (imageUrl && !templateReadOnlyRef.current) {
              // Insert the image at cursor position with permanent URL
              const tempPos = currentEditor.state.selection.anchor;
              currentEditor.chain().insertContentAt(tempPos, {
                type: 'image',
                attrs: {
                  src: imageUrl,
                  alt: file.name,
                  title: file.name,
                  userid: userDetails.id,
                },
              }).focus().run()
            }
          }
        },
      }),
      ImageResize,
    ],
    content: '',
    editable: !templateReadOnly && (view === 'report' ? edit : view === 'summarizer' ? false : true),
    onUpdate: ({ editor }) => {
      handleEditorUpdate(editor);
    }
  });
  useEffect(() => {
    if (view === 'template' && editor) editor.setEditable(!templateReadOnly);
  }, [editor, view, templateReadOnly]);

  const handleEditorUpdate = (editorInstance) => {
    if (view !== 'report' || !edit || !id) return;
    if (isSettingContentRef.current) return;

    const html = editorInstance.getHTML();
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    pendingSaveRef.current = null;
    if (html === lastSavedContentRef.current && !pendingSavesRef.current) {
      onDirtyChange?.(false);
      return;
    }
    onDirtyChange?.(true);
    const persist = () => { pendingSaveRef.current = null; return saveReportContent(html); };
    pendingSaveRef.current = persist;
    saveTimerRef.current = setTimeout(persist, 700);
  };

  const saveReportContent = (content) => {
    if (view !== 'report' || !id) return;
    pendingSavesRef.current += 1;
    const persist = async () => {
      try {
        // A brand-new typed document has no row until its first save (Write).
        if (ensureReport) await ensureReport();
        const updated_at = new Date().toISOString();
        const templateData = { content, updated_at };

        const { data, error } = await supabase
          .from('generatedreports')
          .update(templateData)
          .eq('id', id).select('id');
        if (error) throw error;
        if (!data?.length) throw new Error('The report could not be saved. It may have been deleted or access may have changed.');

        lastSavedContentRef.current = content;
        onDirtyChange?.(pendingSavesRef.current > 1 || Boolean(pendingSaveRef.current) || editor?.getHTML() !== content);

        setHistoryItems((prev) => {
          const updated = [...prev];
          const index = updated.findIndex((item) => item.id === id);
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              originalData: {
                ...updated[index].originalData,
                content: content || updated[index].originalData.content,
              },
            };
          }
          return updated;
        });

        if (setSelectedReport) {
          setSelectedReport((prev) => prev?.id !== id ? prev : ({
            ...prev,
            originalData: {
              ...prev.originalData,
              content: content || prev.originalData.content,
            },
          }));
        }

        if (setSectionSummary) {
          setSectionSummary((prev) => prev?.id !== id ? prev : ({
            ...prev,
            content: content || prev.content,
          }));
        }
      } catch (err) {
        console.error('Error auto-saving report:', err);
        onSaveError?.();
        if (onDirtyChange) setAlert('Report changes could not be saved. Keep this document open and try editing again.', 'error');
      } finally { pendingSavesRef.current -= 1; }
    };
    saveQueueRef.current = saveQueueRef.current.then(persist, persist);
    return saveQueueRef.current;
  };

  useEffect(() => () => {
    clearTimeout(saveTimerRef.current);
    // A workflow change can unmount the editor before the debounce fires.
    // Flush the captured draft to its original report rather than lose it.
    pendingSaveRef.current?.();
  }, [id]);


  useEffect(() => {
    if (editor && view === 'summarizer' && highlightedIndex > 0) {
      // console.log('Scrolling to highlight at index:', highlightedIndex);
      // Wait a bit for render to complete
      setTimeout(() => {
        const dom = editor.view.dom;
        
        // Find all mark elements in the editor
        const allMarks = dom.querySelectorAll('mark');
        
        // Get the mark at the specific index (subtract 1 for zero-based)
        const targetIndex = highlightedIndex - 1;
        const highlightedElement = allMarks[targetIndex];
        // console.log('Highlighted element:', highlightedElement);
        
    
        if (highlightedElement) {
          try {
            // Get the container element - this limits scrolling to just this container
            const container = dom.closest('#template-view-container') || 
                                 dom.closest('[id="template-view-container"]') ||
                                 dom.parentElement; // Fallback to editor's parent
            // console.log('Container for scrolling:', container);
            
            if (container) {              
              // Calculate position within the container
              const elementRect = highlightedElement.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              
              // Calculate the element's position relative to the container
              const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
              const topOffset = containerRect.height * 0.25;
              
              // Scroll just the container, not the whole page
              container.scrollTo({
                top: relativeTop - topOffset,
                behavior: 'smooth'
              });
              
              // Remove selection after scrolling completes
              setTimeout(() => {
                editor.commands.blur();
              }, 300);
            } else {
              // Fallback if container not found
              highlightedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          } catch (err) {
            console.error('Error scrolling to highlight:', err);
            
            // Last resort fallback
            highlightedElement.scrollIntoView({
              behavior: 'auto',
              block: 'center'
            });
          }
        } else {
          console.warn('No highlight element found at index:', highlightedIndex);
        }
      }, 100);
    }
  }, [editor, highlightedIndex, view, currentFileIndex]);

  useEffect(() => {
    if (!editor) return;

    const nextContent =
      templateContent ??
      reportContent ??
      sectionSummary?.content ??
      selectedReport?.originalData?.content ??
      '';

    if (!nextContent && !isEditing) return;

    const current = editor.getHTML();
    if (onDirtyChange && edit && current !== lastSavedContentRef.current && lastSavedContentRef.current && !isSettingContentRef.current) return;
    if (current === nextContent && !isEditing) return;

    if (templateContent) {
      isSettingContentRef.current = true;
      editor.commands.setContent(templateContent);
      lastSavedContentRef.current = templateContent;
      setTitle(templateTitle || '');
      setTimeout(() => (isSettingContentRef.current = false), 0);
      return;
    }

    if (id && reportContent) {
      if (current === reportContent) return;
      isSettingContentRef.current = true;
      editor.commands.setContent(reportContent);
      lastSavedContentRef.current = reportContent;
      setTimeout(() => (isSettingContentRef.current = false), 0);
      return;
    }

    if (isEditing) {
      fetchData(id);
      return;
    }

    if ((view === 'report' || view === 'summarizer') && !id && reportContent) {
      isSettingContentRef.current = true;
      editor.commands.setContent(reportContent);
      lastSavedContentRef.current = reportContent;
      setTimeout(() => (isSettingContentRef.current = false), 0);
    }
  }, [id, isEditing, sectionSummary, selectedReport, reportContent, templateContent, templateTitle, view]);

  useEffect(() => {
    // Skip the first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if(view === 'report') {
      editor.setEditable(edit);
      if(edit === false){
        handleSave();
      }
    }
  }, [edit]);

  const fetchData = async (id) => {
    try {
      setLoading(true);
      if (templateReadOnly) {
        setTitle('');
        editor?.commands.setContent('');
      }
      let data, error;
    
      if(view === 'template') {
        const result = await supabase
          .from('templates')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        data = result.data;
        error = result.error;
      } else if(view === 'report') {
        const result = await supabase
          .from('generatedreports')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        data = result.data;
        error = result.error;
      }
      
      if (error) throw error;
      if (!data && templateReadOnly) throw new Error('Template unavailable');
      
      if (data) {
        const titleResult = await supabase.rpc('decrypt_text', { encrypted_data: data.title });
        const contentResult = await supabase.rpc('decrypt_text', { encrypted_data: data.content });
        if (titleResult.error || contentResult.error) {
          throw titleResult.error || contentResult.error;
        }
        setTitle(titleResult.data || "");
        // Set editor content
        if (editor && contentResult.data) {
          editor.commands.setContent(contentResult.data);
        }
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setAlert("Error fetching template", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (templateReadOnlyRef.current) return;
    if (!title && !reportTitle) {
      setAlert("Please enter a template title", "error");
      return;
    }

    try {
      setLoading(true);
      const content = editor ? editor.getHTML() : "";
      
      let templateData;
      let result;

      if(isEditing) {
        const updated_at = new Date().toISOString();
        templateData = {
          title: view === 'template' ? title : reportTitle,
          content,
          updated_at: updated_at,
        }
        if(view === 'template') {
          const { data: existingTemplate } = await supabase.from('templates').select('*').eq('id', id).single();
          const decryptedTitle = await supabase.rpc('decrypt_text', { encrypted_data: existingTemplate.title });
          const decryptedContent = await supabase.rpc('decrypt_text', { encrypted_data: existingTemplate.content });
          const decryptedCreatedBy = await supabase.rpc('decrypt_text', { encrypted_data: existingTemplate.created_by });

          if (decryptedTitle.error || decryptedContent.error || decryptedCreatedBy.error) {
            throw decryptedTitle.error || decryptedContent.error || decryptedCreatedBy.error;
          }
          existingTemplate.title = decryptedTitle.data;
          existingTemplate.content = decryptedContent.data;
          existingTemplate.created_by = decryptedCreatedBy.data;

          if (existingTemplate.userid !== userDetails.id) {
            setAlert('Use Copy in Template Builder to create your own template before editing.', 'warning');
            return;
          }
          if(existingTemplate && !existingTemplate.is_base_template && existingTemplate.userid === userDetails.id) {
            result = await supabase.from('templates').update(templateData).eq('id', id);
          }
          else {
            templateData.title = templateData.title === existingTemplate.title ? `${templateData.title} (Copy)` : templateData.title;
            templateData.userid = userDetails.id;
            templateData.created_by = userDetails.name;
            result = await supabase.from('templates').insert([templateData]);
          }
        } 
        else if(view === 'report') {
          result = await supabase.from('generatedreports').update(templateData).eq('id', id);

          // Request the new version without removing the current searchable version.
          const formDataReport = new FormData();
          formDataReport.append('files', []);
          formDataReport.append('patient_id', patient_id);
          formDataReport.append('document_type', 'report');
          formDataReport.append('report_content', templateData.content);
          formDataReport.append('report_id', id);
          formDataReport.append('team_id', currentOrg.id);
          formDataReport.append('file_name', reportTitle);

          axios.post(`${process.env.REACT_APP_RAG_BACKEND_URL}/add_embedding`, formDataReport, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Control-Allow-Origin': '*',
            },
          }).then(response => {
            if(response.data.status === 'failed') {
              setAlert('Failed to save report embedding!', 'error');
            }
          }).catch(error => {
            console.error('Background embedding operation failed:', error);
          });

          setHistoryItems((prev) => {
            const updatedItems = [...prev];
            const index = updatedItems.findIndex(item => item.id === id);
            if (index !== -1) {
              updatedItems[index] = { 
                ...updatedItems[index], 
                title: reportTitle || updatedItems[index].title,
                originalData: {
                  ...updatedItems[index].originalData,
                  content: content || updatedItems[index].originalData.content,
                },
              };
            }
            return updatedItems;
          });
          // console.log("seelected report", selectedReport);
          // console.log('section summary', sectionSummary);
          if(setSelectedReport) {
            setSelectedReport(prevReport => ({
              ...prevReport,
              title: reportTitle || prevReport.title,
              originalData: {
                ...prevReport.originalData,
                content: content || prevReport.originalData.content,
              },
            }));
          }
          if(setSectionSummary) {
            setSectionSummary(prevSummary => ({
              ...prevSummary,
              content: content || prevSummary.content,
            }));
          }
          await fetchData(id);
          setAlert("Report updated successfully", "success");
        } 
      } else {
        templateData = {
          title,
          content,
          userid: userDetails.id,
          created_by: userDetails.name,
        }
        result = await supabase.from('templates').insert([templateData]);
      }
    
      
      const { error } = result;
      if (error) throw error;

      if(view === 'template') {
        setAlert(`Template ${isEditing ? 'updated' : 'saved'} successfully`, "success");
      }
      
    } catch (error) {
      console.error('Error saving template:', error);
      setAlert("Error saving template", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      maxWidth={false} 
      disableGutters={true}
      sx={{ 
        width: '100%',
        height: view === 'template' ? '100vh' : '100%', 
        padding: '0 !important', 
        margin: 0,
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: view === 'report' || view === 'summarizer' ? 1 : 'none',
      }}
    >
      {
        view === 'template' && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexShrink: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button 
                  variant="outlined" 
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  sx={{ 
                    color: ui.colors.brand,
                    borderColor: ui.colors.brand,
                    "&:hover": {
                      borderColor: ui.colors.brandHover,
                    }
                  }}
                >
                  Back
                </Button>
                {!templateReadOnly && <Button
                  variant="contained" 
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ bgcolor: ui.colors.brand }}
                >
                  Save
                </Button>}
              </Stack>
              <IconButton onClick={() => setShowGuidelines(true)}>
                <HelpOutline />
              </IconButton>
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                label="Template Title"
                variant="outlined"
                fullWidth
                value={title}
                InputProps={{ readOnly: templateReadOnly }}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter template title"
                required
              />
            </Box>
          </>
        )
      }
      {
        !templateReadOnly && view !== 'summarizer' && !(view === 'report' && !edit) && (
          <Box sx={{ flexShrink: 0 }}>
            {/* reportTools: toolbar collapses behind the header's Format icon (kept mounted). */}
            <Box sx={{ display: !reportTools || reportTools.format ? 'block' : 'none' }}><MenuBar editor={editor} userDetails={userDetails} /></Box>
            {view === 'report' && edit && <SaveAsTemplateButton userDetails={userDetails} onTemplateSaved={onTemplateSaved} getContent={() => editor?.getHTML() || ''} disabled={!editor} hideTrigger={Boolean(reportTools)} openSignal={reportTools?.saveSignal} />}
          </Box>
        )
      }
      {
        onCheckSources && <Box sx={{ display: !reportTools || reportTools.sources ? 'block' : 'none' }}><ReportSourceSearch editor={editor} onCheck={onCheckSources} checking={checkingSources} /></Box>
      }
      {
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',  flex: 1 }}>
            <Loader />
          </Box>
        ) : (
          <Box 
            id="template-view-container" // Add this ID for the scroll effect to work
            sx={{ 
              border: `1px solid ${ui.colors.border}`,
              borderRadius: `${ui.radius.control}px`,
              p: 2, 
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              position: 'relative',
              maxHeight: view === 'template' ? 'calc(100vh - 200px)' : '100%',
              '& .ProseMirror': {
                minHeight: 'fit-content',
                outline: 'none',
                paddingBottom: '20px',
                height: 'auto',
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
                '& table': {
                  borderCollapse: 'collapse',
                  tableLayout: 'fixed',
                  width: '100%',
                  margin: '1rem 0',
                  position: 'relative',
                },
                '& td, & th': {
                  border: '1px solid black',
                  padding: '0.5rem',
                  position: 'relative',
                  verticalAlign: 'top',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold'
                },
              }
            }}
          >
            <div
              onMouseUp={highlightMode ? handleEditorSelection : null}
              onTouchEnd={highlightMode ? handleEditorSelection : null}
            >
              <EditorContent editor={editor} />
            </div>
          </Box>
        )
      }

      <Dialog open={showGuidelines} onClose={() => setShowGuidelines(false)} maxWidth="md">
        <DialogTitle sx={{ ...ui.typography.heading }}>Template Creation Guidelines</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Section Headings</Typography>
          <Typography paragraph>
            Write headings & sections in plain text to reflect your personal note or document writing style.
            <br />
            <em>e.g. Subjective</em>
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Placeholders</Typography>
          <Typography paragraph>
            Wrap text with [ ] to create areas that should be filled in. For greater flexibility or optional content, use instructions instead.
            <br />
            <em>e.g. [Mention patient's past medical history]</em>
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Verbatim</Typography>
          <Typography paragraph>
            Wrap text with " " to include terms word-for-word every time. This is helpful for things like note signatures and intros.
            <br />
            <em>e.g. "P. Sherman, 42 Wallaby Way, Sydney."</em>
          </Typography>

          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Instructions</Typography>
          <Typography paragraph>
            Wrap instructions in round brackets to indicate on how to treat information that is/is not mentioned in your session.
            <br />
            <em>e.g. (only include if explicitly mentioned in the transcript, contextual notes or clinical note, otherwise leave blank.)</em>
          </Typography>

          <Typography variant="body1" sx={{ mt: 2 }}>
            If your template does not perform as expected, try adding AI instructions at the end of your template referencing the specific sections in your template and what you'd like to do to them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGuidelines(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateView;
